import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from "@angular/material/dialog";
import {AddTerminalDialogComponent} from "../add-terminal-dialog/add-terminal-dialog.component";
import {Sort} from "@angular/material/sort";
import {Terminals} from "../../../shared/interface/terminals";
import {generate, interval, ReplaySubject} from "rxjs";
import {delay, distinctUntilChanged, first, takeUntil, tap} from "rxjs/operators";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {TerminalsService} from "../../../shared/services/terminals.service";
import {CountedAndPaginated} from "../../../shared/interface/response/counted-and-paginated";
import {CommissionMarginReport} from "../../../shared/interface/commission-margin-report";
import {UserPermissions} from "../../../shared/interface/user-permissions";
import {AuthService} from "../../../shared/services/auth.service";
import {select, Store} from "@ngrx/store";
import {getCurrentRouteState} from "../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../shared/ngrx/route-serializer";
import {StoreRootState} from "../../../shared/ngrx/reducers";
import {FilterService} from "../../../shared/services/filter.service";
import {Uuid} from "../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {MatSnackBar} from "@angular/material/snack-bar";
import {
  RECEIVABLES_IMPORT_ROW_STATUS_CANCELLED,
  TERMINAL_STATUS_SUSPENDED,
  TERMINAL_STATUS_TO_BE_REALIZED
} from "../../../shared/services/dict.service";
import {ActivatedRoute, Router} from "@angular/router";
import {NgForm} from "@angular/forms";
import {Contract} from "../../../shared/interface/contract";
import {ConfirmDialogComponent} from "../../../shared/components/confirm-dialog/confirm-dialog.component";
import {TerminalsBankJobService} from "../../../shared/services/terminals-bank-job.service";
import {TerminalsImportRow} from "../../../shared/interface/terminals-import-row";
import {
  ChangePartnerFacilityDialogComponent
} from "../imports-row/change-partner-facility-dialog/change-partner-facility-dialog.component";
import {
  ChangeTerminalsImportRowDialogComponent,
  ChangeTerminalsImportRowDialogData
} from "../change-terminals-import-row-dialog/change-terminals-import-row-dialog.component";
import {
  ConfirmDialogWithDictComponent
} from "../../../shared/components/confirm-dialog-with-dict/confirm-dialog-with-dict.component";

@Component({
  selector: 'app-all-terminals-list',
  templateUrl: './all-terminals-list.component.html',
  styleUrls: ['./all-terminals-list.component.scss']
})
export class AllTerminalsListComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @Input() statusKey?: string[] = null;
  @Input() generate = null;
  @Input() assignedTransaction = null;

  totalData = 0;
  sortBy = 'dateAdded';
  sortOrder = 'desc';
  displayedColumns: string[] = ['policyNumber', 'partner', 'user', 'insuranceCompany', 'accountNumber', 'sum' ,  'dateAdded' , 'payAt', 'status', 'action'];
  terminals: Terminals[] = [];
  userPermissions: ReplaySubject<UserPermissions>;
  initialFilters: any;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private dialog: MatDialog,
    private terminalService: TerminalsService,
    private auth: AuthService,
    private store: Store<StoreRootState>,
    private filterService: FilterService,
    private snack: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private terminalsBankJobService: TerminalsBankJobService
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.terminalService.getTerminalListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Terminals>) => {
          this.terminals = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  ngAfterViewInit(): void {
    this.resetFilters();
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.statusKey = this.statusKey ?? data.statusKey ?? null;
        this.generate = this.generate ?? data.generate ?? null;
        this.assignedTransaction   = this.assignedTransaction ?? data.assignedTransaction ?? null;

        this.store
          .pipe(
            select(getCurrentRouteState),
            delay(0),
            distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
            takeUntil(this.destroyed$),
          )
          .subscribe((state: RouterStateUrl) => {
            this.parseFilters(state.params);
            this.paginator.pageIndex = state.params.page;
            this.paginator.pageSize = state.params.pageSize;
            this.loadListData();
          })
        ;
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });
  }

  ngOnInit(): void {
    this.parseFilters();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  addTerminal(): void {
    const dialogRef = this.dialog.open(AddTerminalDialogComponent, {
      width: '50vw',
      maxHeight: '90vh',
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadListData();
    });


  }

  generateFile(): void {
    const dialogRef = this.dialog.open(ConfirmDialogWithDictComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz wygenerować plik do banku?', label: 'Bank', dictType: 'bank_type'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.accepted) {

        this.terminalsBankJobService.generate(result.reasonId)
          .subscribe(() => {
            this.router.navigate(['/terminale/przelewy/przelewy/lista']);
          });
      }
    });

  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;
    this.loadListData();
  }

  loadListData(): void {
    this.terminalService.loadList(this.buildFilters(),
      this.paginator.pageIndex, this.paginator.pageSize,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
    );

    this.terminalService.loadNumberOfTerminals(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFiltersNumber(TERMINAL_STATUS_TO_BE_REALIZED),
      this.buildFiltersNumber(TERMINAL_STATUS_SUSPENDED));
  }

  numberOfTerminalToBeRealized(): number{
    return this.terminalService.getNumberOfTerminalsToBeRealized();
  }
  numberOfTerminalSuspended(): number{
    return  this.terminalService.getNumberOfTerminalsSuspended();
  }

  trackTask(index: number, terminals: Terminals): string {
    return terminals.uuid;
  }


  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      in$insuranceCompany: 'int[]',
      partner: 'string',
      lte$sum: 'string',
      gte$sum: 'string',
      status: 'int',
      contains$policyNumber: 'string',
      contains$accountNumber: 'string',
      assignedTransaction: 'int',
      gte$dateAdded: 'date',
      lte$dateAdded: 'date',
      gte$paymentAt: 'date',
      lte$paymentAt: 'date',
    }, filters);
  }

  buildFilters(): any {
    const filters = {...this.initialFilters};

    if (null !== this.statusKey) {
      filters.statusKey = this.statusKey;
    }

    if (null !== this.assignedTransaction) {
      filters.assignedTransaction = this.assignedTransaction;
    }

    return filters;
  }

  buildFiltersNumber(key: string): any {
    const filters = {...this.initialFilters};
    filters.statusKey = [key];
    filters.assignedTransaction = this.assignedTransaction;
    return filters;
  }

  downloadReport(): void {
      this.terminalService.downloadReport(this.buildFilters())
        .subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });
        }, (error: HttpErrorResponse) => {
          this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
        });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }
  terminalImportRowChange(terminals: Terminals): void {
    const dialogRef = this.dialog.open(ChangeTerminalsImportRowDialogComponent, {
      width: '700px',
      data: {
        terminalsObject: terminals
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadListData();
    });
  }
}


